import { createMemo, createSignal, For, Show } from "solid-js";
import {
  getAdminRuntimeConfig,
  getAvailableTargetProfiles,
  getCustomTargetProfiles,
  getSelectedTargetProfileId,
  saveCustomTargetProfiles,
  setSelectedTargetProfileId,
  type AdminTargetProfile,
} from "../runtime";

function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || `target-${Date.now()}`;
}

export function TargetProfilePicker(props: { compact?: boolean }) {
  const [open, setOpen] = createSignal(false);
  const [editing, setEditing] = createSignal(false);
  const [profilesVersion, setProfilesVersion] = createSignal(0);
  const [label, setLabel] = createSignal("");
  const [target, setTarget] = createSignal<"cloudflare" | "vps">("cloudflare");
  const [mode, setMode] = createSignal<"staging" | "production">("production");
  const [controlPlaneBaseUrl, setControlPlaneBaseUrl] = createSignal("");
  const [managedAppBaseUrl, setManagedAppBaseUrl] = createSignal("");
  const [webAppBaseUrl, setWebAppBaseUrl] = createSignal("");

  const profiles = createMemo(() => {
    profilesVersion();
    return getAvailableTargetProfiles();
  });
  const selectedId = createMemo(() => {
    profilesVersion();
    return getSelectedTargetProfileId();
  });
  const selected = createMemo(() => profiles().find((profile) => profile.id === selectedId()) ?? null);
  const runtime = createMemo(() => {
    profilesVersion();
    return getAdminRuntimeConfig();
  });

  function applyProfile(profileId: string) {
    setSelectedTargetProfileId(profileId);
    window.location.reload();
  }

  function saveProfile() {
    const nextProfile: AdminTargetProfile = {
      id: slugify(label()),
      label: label().trim(),
      controlPlaneBaseUrl: controlPlaneBaseUrl().trim().replace(/\/+$/, ""),
      managedAppBaseUrl: managedAppBaseUrl().trim().replace(/\/+$/, ""),
      webAppBaseUrl: webAppBaseUrl().trim().replace(/\/+$/, ""),
      controlPlane: {
        target: target(),
        mode: mode(),
        authMode: "password",
        authRequired: true,
        supportsDirectDbAccess: true,
        supportsProcessControl: target() === "vps",
        supportsAuditLog: true,
      },
    };
    if (!nextProfile.label || !nextProfile.controlPlaneBaseUrl || !nextProfile.managedAppBaseUrl || !nextProfile.webAppBaseUrl) {
      return;
    }
    const existing = getCustomTargetProfiles().filter((profile) => profile.id !== nextProfile.id);
    saveCustomTargetProfiles([...existing, nextProfile]);
    setProfilesVersion((value) => value + 1);
    setEditing(false);
    setOpen(false);
    applyProfile(nextProfile.id);
  }

  function removeProfile(profileId: string) {
    const next = getCustomTargetProfiles().filter((profile) => profile.id !== profileId);
    saveCustomTargetProfiles(next);
    if (selectedId() === profileId) {
      const fallback = profiles().find((profile) => profile.builtin)?.id ?? profiles()[0]?.id;
      if (fallback) {
        setSelectedTargetProfileId(fallback);
      }
    }
    setProfilesVersion((value) => value + 1);
    window.location.reload();
  }

  return (
    <div class="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        class={props.compact
          ? "min-w-0 sm:min-w-56 rounded-xl border border-gray-700 bg-gray-900/80 px-3 py-2 text-left text-gray-200 hover:bg-gray-800 cursor-pointer"
          : "min-w-64 rounded-xl border border-gray-700 bg-gray-900/80 px-3 py-2 text-left text-gray-200 hover:bg-gray-800 transition-colors cursor-pointer"}
      >
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0">
            <div class="text-[11px] uppercase tracking-wide text-gray-500">Environment</div>
            <div class="truncate text-sm font-semibold text-gray-100">{selected()?.label ?? "Target"}</div>
          </div>
          <div class="shrink-0 text-right">
            <div class="text-[11px] uppercase tracking-wide text-cyan-400">{selected()?.controlPlane?.target ?? "vps"}</div>
            <div class="text-xs text-gray-500">{selected()?.controlPlane?.mode ?? "local"}</div>
          </div>
        </div>
      </button>

      <Show when={open()}>
        <div class="fixed sm:absolute left-2 right-2 sm:left-auto sm:right-0 top-12 sm:top-full z-50 sm:mt-2 sm:w-[28rem] max-h-[90vh] overflow-y-auto rounded-xl border border-gray-600 bg-gray-950 shadow-2xl">
          <div class="relative border-b border-gray-800 px-4 py-3">
            <div class="text-[11px] uppercase tracking-wider text-gray-500">Active Environment</div>
            <button
              onClick={() => setOpen(false)}
              class="absolute top-3 right-3 rounded border border-gray-700 px-2 py-0.5 text-xs text-gray-500 hover:border-red-700 hover:text-red-400 cursor-pointer"
            >
              ✕
            </button>
            <div class="mt-2 flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="truncate text-base font-semibold text-gray-100">{selected()?.label ?? "Target"}</div>
                <div class="mt-1 flex flex-wrap gap-2 text-[11px]">
                  <span class="rounded-full border border-cyan-800 bg-cyan-950/30 px-2 py-0.5 text-cyan-300">
                    {selected()?.controlPlane?.target ?? "vps"}
                  </span>
                  <span class="rounded-full border border-gray-700 bg-gray-800 px-2 py-0.5 text-gray-300">
                    {selected()?.controlPlane?.mode ?? "local"}
                  </span>
                </div>
              </div>
              <div class="max-w-44 shrink-0 text-right text-[11px] text-gray-500">
                <div>Control plane</div>
                <div class="mt-1 break-all text-gray-300">{runtime().controlPlaneBaseUrl}</div>
              </div>
            </div>
          </div>

          <div class="border-b border-gray-800 px-4 py-3">
            <div class="text-[11px] uppercase tracking-wider text-gray-500">Connected Services</div>
            <dl class="mt-2 space-y-2 text-xs">
              <div class="flex items-start justify-between gap-3">
                <dt class="shrink-0 text-gray-500">Managed API</dt>
                <dd class="break-all text-right text-gray-200">{runtime().managedAppBaseUrl}</dd>
              </div>
              <div class="flex items-start justify-between gap-3">
                <dt class="shrink-0 text-gray-500">Managed Web</dt>
                <dd class="break-all text-right text-gray-200">{runtime().webAppBaseUrl}</dd>
              </div>
            </dl>
          </div>

          <div class="border-b border-gray-800 px-4 py-3">
            <div class="text-[11px] uppercase tracking-wider text-gray-500">Available Environments</div>
          </div>
          <div class="max-h-72 overflow-auto divide-y divide-gray-800">
            <For each={profiles()}>
              {(profile) => (
                <div class="px-4 py-3">
                  <div class="flex items-start justify-between gap-3">
                    <button onClick={() => applyProfile(profile.id)} class="flex-1 cursor-pointer text-left">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium text-gray-100">{profile.label}</span>
                        <Show when={profile.id === selectedId()}>
                          <span class="rounded border border-cyan-700 px-2 py-0.5 text-[10px] text-cyan-300">Active</span>
                        </Show>
                        <Show when={profile.builtin}>
                          <span class="rounded border border-gray-700 px-2 py-0.5 text-[10px] text-gray-400">Built-in</span>
                        </Show>
                      </div>
                      <div class="mt-1 text-xs text-gray-500">
                        {profile.controlPlane?.target ?? "vps"} · {profile.controlPlane?.mode ?? "production"}
                      </div>
                      <div class="mt-1 break-all text-[11px] text-gray-600">{profile.controlPlaneBaseUrl}</div>
                    </button>
                    <Show when={!profile.builtin}>
                      <button
                        onClick={() => removeProfile(profile.id)}
                        class="cursor-pointer rounded border border-red-800 px-2 py-1 text-xs text-red-300 hover:bg-red-950/40"
                      >
                        Remove
                      </button>
                    </Show>
                  </div>
                </div>
              )}
            </For>
          </div>

          <div class="border-t border-gray-800 px-4 py-3">
            <button onClick={() => setEditing((value) => !value)} class="w-full cursor-pointer text-left text-xs text-cyan-300 hover:text-cyan-200">
              {editing() ? "Hide custom target form" : "Add custom remote target"}
            </button>
          </div>
          <Show when={editing()}>
            <div class="space-y-3 border-t border-gray-800 px-4 py-3">
              <input
                value={label()}
                onInput={(e) => setLabel(e.currentTarget.value)}
                placeholder="Label (e.g. Cloudflare Prod)"
                class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 outline-none focus:border-cyan-600"
              />
              <div class="grid grid-cols-2 gap-2">
                <select value={target()} onChange={(e) => setTarget(e.currentTarget.value as "cloudflare" | "vps")} class="rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100">
                  <option value="cloudflare">Cloudflare</option>
                  <option value="vps">VPS</option>
                </select>
                <select value={mode()} onChange={(e) => setMode(e.currentTarget.value as "staging" | "production")} class="rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100">
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                </select>
              </div>
              <input
                value={controlPlaneBaseUrl()}
                onInput={(e) => setControlPlaneBaseUrl(e.currentTarget.value)}
                placeholder="Control plane base URL"
                class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 outline-none focus:border-cyan-600"
              />
              <input
                value={managedAppBaseUrl()}
                onInput={(e) => setManagedAppBaseUrl(e.currentTarget.value)}
                placeholder="Managed API base URL"
                class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 outline-none focus:border-cyan-600"
              />
              <input
                value={webAppBaseUrl()}
                onInput={(e) => setWebAppBaseUrl(e.currentTarget.value)}
                placeholder="Managed web base URL"
                class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 outline-none focus:border-cyan-600"
              />
              <button onClick={saveProfile} class="w-full cursor-pointer rounded bg-cyan-500 px-3 py-2 text-sm font-semibold text-gray-950 hover:bg-cyan-400">
                Save and switch
              </button>
            </div>
          </Show>
        </div>
        <div class="fixed inset-0 z-40" onClick={() => setOpen(false)} />
      </Show>
    </div>
  );
}
